/**
 * SEARCH WORKSPACE — /google-ads/leads and /google-ads/traffic.
 *
 * Same look as the YouTube workspace (toolbar · stat tiles · filter bar ·
 * campaign → ad group → ad rows · list / grid), built for Search only:
 * each objective lists its own campaigns (what the CRM recorded when it
 * built them; older ones are sorted by lead form and bidding), ad groups
 * show their keywords, ads read like a Google result, and every "add"
 * button stays inside this objective's builders.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  ArrowUpDown, BarChart3, ChevronDown, ChevronRight, Copy, FileText, Globe, Info, Layers, LayoutGrid, List, Megaphone, Pause, Play,
  Plus, RefreshCw, Search, type LucideIcon,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { EmptyState } from '@/components/EmptyState';
import { AttachCustomerDialog } from '@/components/google-ads/AttachCustomerDialog';
import { ReuseAdDialog } from '@/components/google-ads/ReuseAdDialog';
import '@/components/google-ads/google-ads.css';
import {
  googleAdsService, GoogleAdsAdGroupRow, GoogleAdsAdRow, GoogleAdsCampaignSummary, SearchKeywordRow, SearchObjectiveKey,
} from '@/services/google-ads.service';
import {
  adGroupAdResourceName, alreadyToasted, approvalOf, biddingLabel, CampaignDates, errMsg, formatMicros, newestFirst, Pill, Spinner,
  statusOf, Switch, Tag, useYoutubeAuth, type YtAuthStatus,
} from '@/components/google-ads/youtube/youtubeShared';
import { isSearchCampaign, searchObjectiveOf, SearchObjectiveConfig } from './searchShared';

type Counts = { byCampaign: Record<string, { adGroups: number; ads: number; assetGroups?: number }>; byAdGroup: Record<string, number> };

const OTHER: Record<SearchObjectiveKey, { title: string; base: string }> = {
  leads: { title: 'Website traffic', base: '/google-ads/traffic' },
  traffic: { title: 'Leads', base: '/google-ads/leads' },
};

const hostOfUrl = (u?: string) => { try { return u ? new URL(u).hostname.replace(/^www\./, '') : ''; } catch { return ''; } };
const matchSyntax = (text: string, m: string) => (m === 'EXACT' ? `[${text}]` : m === 'PHRASE' ? `"${text}"` : text);

function Skeleton({ count = 3, indent = '' }: { count?: number; indent?: string }) {
  return <div className={clsx('space-y-2 py-2', indent)}>{Array.from({ length: count }, (_, i) => <div key={i} className="skeleton h-10 rounded-xl" />)}</div>;
}

/** A Google-result-style card for one responsive search ad. */
function MiniSerp({ ad, compact }: { ad: GoogleAdsAdRow; compact?: boolean }) {
  const host = hostOfUrl(ad.finalUrls[0]) || 'yourwebsite.com';
  return (
    <div className="rounded-lg border border-border bg-white p-2.5 text-[#202124] dark:bg-[#1f1f1f] dark:text-[#e8eaed]">
      <div className="flex items-center gap-1.5 text-[10px]">
        <span className="font-bold">Sponsored</span><span className="text-[#5f6368] dark:text-[#9aa0a6]">· {host}</span>
      </div>
      <div className={clsx('mt-0.5 line-clamp-2 leading-snug text-[#1a0dab] dark:text-[#8ab4f8]', compact ? 'text-[12px]' : 'text-[13px]')}>
        {ad.headlines.slice(0, 3).join(' | ') || 'Responsive search ad'}
      </div>
      {!compact && ad.descriptions?.[0] && <div className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-[#4d5156] dark:text-[#bdc1c6]">{ad.descriptions.slice(0, 2).join(' ')}</div>}
    </div>
  );
}

// ── ads ──────────────────────────────────────────────────────────────────

function AdRows({ accountUid, customerId, adGroupId, parentPaused }: { accountUid: string; customerId: string; adGroupId: string; parentPaused: boolean }) {
  const [ads, setAds] = useState<GoogleAdsAdRow[] | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => {
    setError('');
    try { setAds((await googleAdsService.listAds(accountUid, adGroupId)).filter((a) => !!a.adId)); } catch (e) { setError(errMsg(e)); }
  }, [accountUid, adGroupId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (ad: GoogleAdsAdRow) => {
    setBusy(ad.adId);
    const rn = adGroupAdResourceName(customerId, ad.adGroupId, ad.adId);
    try {
      if (ad.status === 'ENABLED') await googleAdsService.pauseAd(accountUid, rn); else await googleAdsService.resumeAd(accountUid, rn);
      await load();
    } catch (e) { if (!alreadyToasted(e)) toast.error(`Google refused — ${errMsg(e)}`); } finally { setBusy(null); }
  };

  if (error) return <div className="py-2 pl-9 text-xs text-danger">{error} <button type="button" className="ml-1 underline" onClick={load}>Retry</button></div>;
  if (ads === null) return <Skeleton count={2} indent="pl-9" />;
  if (ads.length === 0) return <div className="py-3 pl-9 text-sm text-text-muted">No ads yet.</div>;
  return (
    <div className="pl-9">
      {ads.map((ad) => {
        const st = ad.status === 'ENABLED' && parentPaused ? { label: 'Parent paused', tone: 'paused' as const } : statusOf(ad.status);
        const approval = approvalOf(ad.approvalStatus, ad.reviewStatus);
        const host = hostOfUrl(ad.finalUrls[0]);
        return (
          <div key={ad.adId} className="flex items-center gap-3.5 border-t border-border/40 py-3 first:border-t-0">
            <span className="flex h-9 w-16 shrink-0 flex-col items-center justify-center rounded-md border border-border bg-white text-[#1a0dab] dark:bg-[#1f1f1f] dark:text-[#8ab4f8]">
              <span className="text-[8px] font-bold text-[#202124] dark:text-[#e8eaed]">Ad</span>
              <Search size={11} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-text-primary">{ad.headlines.slice(0, 2).join(' | ') || ad.name || `Ad ${ad.adId}`}</div>
              <div className="mt-0.5 truncate text-xs text-text-muted">
                {[host, `${ad.headlines.length} headlines`, `${ad.descriptions?.length ?? 0} descriptions`].filter(Boolean).join(' · ')}
              </div>
            </div>
            {approval && <Pill {...approval} className="hidden sm:inline-flex" />}
            <Pill {...st} />
            <Switch checked={ad.status === 'ENABLED'} disabled={busy === ad.adId || ad.status === 'REMOVED'} label="Enable ad" onCheckedChange={() => toggle(ad)} />
          </div>
        );
      })}
    </div>
  );
}

// ── ad groups ────────────────────────────────────────────────────────────

function Keywords({ accountUid, adGroupId }: { accountUid: string; adGroupId: string }) {
  const [data, setData] = useState<{ keywords: SearchKeywordRow[]; negatives: SearchKeywordRow[] } | null>(null);
  useEffect(() => {
    let alive = true;
    googleAdsService.search.adGroupKeywords(accountUid, adGroupId)
      .then((r) => { if (alive) setData(r); })
      .catch(() => { if (alive) setData({ keywords: [], negatives: [] }); });
    return () => { alive = false; };
  }, [accountUid, adGroupId]);
  if (data === null) return <div className="flex items-center gap-1.5 py-2 pl-9 text-2xs text-text-muted"><Spinner size={11} /> Loading keywords…</div>;
  if (data.keywords.length === 0) return <div className="py-2 pl-9 text-2xs text-text-muted">No keywords — this ad group can't show on searches.</div>;
  return (
    <div className="space-y-1.5 py-2 pl-9">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-2xs font-semibold uppercase tracking-wide text-text-muted">Keywords</span>
        {data.keywords.slice(0, 14).map((k) => (
          <span key={k.criterionId} className="rounded-full border border-border bg-surface px-2 py-0.5 text-2xs text-text-secondary">{matchSyntax(k.text, k.matchType)}</span>
        ))}
        {data.keywords.length > 14 && <span className="text-2xs text-text-muted">+{data.keywords.length - 14} more</span>}
      </div>
      {data.negatives.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-2xs font-semibold uppercase tracking-wide text-text-muted">Negatives</span>
          {data.negatives.slice(0, 10).map((k) => (
            <span key={k.criterionId} className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs text-text-muted">−{k.text}</span>
          ))}
          {data.negatives.length > 10 && <span className="text-2xs text-text-muted">+{data.negatives.length - 10} more</span>}
        </div>
      )}
    </div>
  );
}

function AdGroupRows({ accountUid, customerId, campaign, currency, base, adCounts, onChanged }: {
  accountUid: string; customerId: string; campaign: GoogleAdsCampaignSummary; currency: string | null; base: string;
  adCounts?: Record<string, number>; onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<GoogleAdsAdGroupRow[] | null>(null);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [reuse, setReuse] = useState<GoogleAdsAdGroupRow | null>(null);
  const [revision, setRevision] = useState(0);
  const load = useCallback(async () => {
    setError('');
    try { setGroups((await googleAdsService.listAdGroups(accountUid, campaign.id)).filter((g) => !!g.id)); } catch (e) { setError(errMsg(e)); }
  }, [accountUid, campaign.id]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (g: GoogleAdsAdGroupRow) => {
    setBusy(g.id);
    try {
      if (g.status === 'ENABLED') await googleAdsService.pauseAdGroup(accountUid, g.id); else await googleAdsService.resumeAdGroup(accountUid, g.id);
      await load();
    } catch (e) { if (!alreadyToasted(e)) toast.error(`Google refused — ${errMsg(e)}`); } finally { setBusy(null); }
  };

  if (error) return <div className="py-2 text-xs text-danger">{error} <button type="button" className="ml-1 underline" onClick={load}>Retry</button></div>;
  if (groups === null) return <Skeleton count={2} indent="pl-8" />;
  const manual = (campaign.biddingStrategyType ?? '').startsWith('MANUAL_CPC');
  return (
    <div className="space-y-1 pl-2">
      {reuse && <ReuseAdDialog accountUid={accountUid} group={reuse} onClose={() => setReuse(null)}
        onSaved={() => { setOpenId(reuse.id); setRevision((v) => v + 1); setReuse(null); onChanged(); }} />}
      {groups.length === 0 && <div className="py-2 text-sm text-text-muted">No ad groups yet — the campaign can't show without one.</div>}
      {groups.map((g) => {
        const open = openId === g.id;
        const st = statusOf(g.status, g.primaryStatus);
        const cpc = manual ? formatMicros(g.cpcBidMicros, currency) : null;
        const n = adCounts?.[g.id];
        return (
          <div key={g.id} className={clsx('rounded-xl border transition-colors', open ? 'border-border-strong bg-surface' : 'border-border/70 bg-surface hover:border-border-strong')}>
            <div className="flex items-center gap-2 px-3 py-2">
              <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left" onClick={() => setOpenId(open ? null : g.id)} aria-expanded={open}>
                {open ? <ChevronDown size={16} className="shrink-0 text-text-muted" /> : <ChevronRight size={16} className="shrink-0 text-text-muted" />}
                <Layers size={16} className="shrink-0 text-text-muted/70" />
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-text-primary">{g.name}</div>
                  <div className="truncate text-xs text-text-muted">
                    Search ad group{cpc ? ` · max CPC ${cpc}` : ''}{n !== undefined ? ` · ${n} ad${n === 1 ? '' : 's'}` : ''}
                  </div>
                </div>
              </button>
              {g.status !== 'REMOVED' && (
                <Button variant="ghost" size="sm" className="hidden md:inline-flex" iconLeft={<Copy size={13} />} title="Copy a compatible ad into this group" onClick={() => setReuse(g)}>
                  Use existing ad
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-8 gap-1 px-2.5 text-sm text-text-secondary hover:text-text-primary"
                onClick={() => navigate(`${base}/create/ad?adgroup_id=${g.id}`)}>
                <Plus size={13} /> Ad
              </Button>
              <Pill {...st} />
              <Switch checked={g.status === 'ENABLED'} disabled={busy === g.id || g.status === 'REMOVED'} label={`Enable ${g.name}`} onCheckedChange={() => toggle(g)} />
            </div>
            {open && (
              <div className="border-t border-border/50 px-3.5 pb-2">
                <Keywords accountUid={accountUid} adGroupId={g.id} />
                <AdRows key={`${g.id}-${revision}`} accountUid={accountUid} customerId={customerId} adGroupId={g.id}
                  parentPaused={campaign.status !== 'ENABLED' || g.status !== 'ENABLED'} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ConnectGate({ auth, accountUid, onChanged }: { auth: YtAuthStatus; accountUid: string; onChanged: () => void }) {
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);
  if (!auth.connected) {
    return (
      <EmptyState icon={<Globe size={40} className="text-text-muted" />} title="No Google account connected" description="Connect Google in the Account page first."
        action={<Button onClick={() => navigate('/google-ads/account')} iconLeft={<Globe size={14} />}>Connect Google Ads</Button>} />
    );
  }
  return (
    <>
      <EmptyState icon={<Globe size={40} className="text-text-muted" />} title="Customer not attached yet" description="Pick which Google Ads customer this connection manages."
        action={<Button onClick={() => setPickerOpen(true)} iconLeft={<Plus size={14} />}>Choose account</Button>} />
      <AttachCustomerDialog open={pickerOpen} accountUid={accountUid} onClose={() => setPickerOpen(false)} onAttached={() => { setPickerOpen(false); onChanged(); }} />
    </>
  );
}

// ── workspace ────────────────────────────────────────────────────────────

export default function SearchWorkspace({ objective: cfg }: { objective: SearchObjectiveConfig }) {
  const navigate = useNavigate();
  const { auth, account, accountUid, checkAuth } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);
  const customerId = account?.customerId ?? '';
  const currency = auth?.currency ?? null;
  const newRoute = `${cfg.base}/create/campaign`;
  const viewKey = `gads_${cfg.key}_view`;

  const [campaigns, setCampaigns] = useState<GoogleAdsCampaignSummary[] | null>(null);
  const [leadFormIds, setLeadFormIds] = useState<Set<string>>(() => new Set());
  const [counts, setCounts] = useState<Counts | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<'newest' | 'name' | 'budget'>('newest');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'paused' | 'removed'>('all');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previews, setPreviews] = useState<Record<string, GoogleAdsAdRow | null>>({});
  const [view, setView] = useState<'list' | 'grid'>(() => {
    try { return (localStorage.getItem(viewKey) as 'list' | 'grid') || 'list'; } catch { return 'list'; }
  });
  const setViewPersisted = (v: 'list' | 'grid') => {
    setView(v);
    try { localStorage.setItem(viewKey, v); } catch { /* storage unavailable */ }
  };

  const load = useCallback(async () => {
    if (!ready) return;
    setListError(null);
    setRefreshing(true);
    try {
      const [list, forms, tags] = await Promise.all([
        googleAdsService.listCampaigns(accountUid),
        googleAdsService.search.leadFormCampaigns(accountUid).catch(() => [] as string[]),
        googleAdsService.search.campaignObjectives(accountUid).catch(() => ({} as Record<string, SearchObjectiveKey>)),
      ]);
      const lf = new Set(forms);
      setLeadFormIds(lf);
      setCampaigns(newestFirst(list.filter((c) => isSearchCampaign(c) && searchObjectiveOf(c, tags, lf) === cfg.key)));
    } catch (e) {
      setListError(errMsg(e, 'Could not load campaigns.'));
      setCampaigns([]);
    } finally { setRefreshing(false); }
    googleAdsService.campaignCounts(accountUid).then(setCounts).catch(() => undefined);
  }, [ready, accountUid, cfg.key]);
  // customerId: re-attaching a different customer on the same account must refetch.
  useEffect(() => { load(); }, [load, customerId]);

  const live = useMemo(() => (campaigns ?? []).filter((c) => c.status !== 'REMOVED'), [campaigns]);
  const removed = useMemo(() => (campaigns ?? []).filter((c) => c.status === 'REMOVED'), [campaigns]);
  const visible = useMemo(() => (statusFilter === 'removed' ? removed : live)
    .filter((c) => statusFilter === 'all' || statusFilter === 'removed' || c.status === (statusFilter === 'active' ? 'ENABLED' : 'PAUSED'))
    .filter((c) => `${c.name} ${c.id}`.toLowerCase().includes(query.trim().toLowerCase()))
    .sort((a, b) => (sort === 'name' ? a.name.localeCompare(b.name) : sort === 'budget' ? Number(b.budgetAmountMicros || 0) - Number(a.budgetAmountMicros || 0) : 0)),
  [live, removed, statusFilter, query, sort]);

  // Grid cards preview each campaign's first ad — one request pair for all of them.
  useEffect(() => {
    if (view !== 'grid' || live.length === 0 || !accountUid) return;
    let alive = true;
    (async () => {
      try {
        const [groups, ads] = await Promise.all([googleAdsService.listAdGroups(accountUid), googleAdsService.listAds(accountUid)]);
        const campaignOf = new Map(groups.map((g) => [g.id, g.campaignId]));
        if (alive) setPreviews(Object.fromEntries(live.map((c) => [c.id, ads.find((a) => campaignOf.get(a.adGroupId) === c.id && a.headlines.length > 0) ?? null])));
      } catch { /* decoration only */ }
    })();
    return () => { alive = false; };
  }, [view, live, accountUid]);

  const toggle = async (c: GoogleAdsCampaignSummary) => {
    const enable = c.status !== 'ENABLED';
    if (enable && !window.confirm(`Enable "${c.name}"? It will start spending its budget.`)) return;
    setBusy(c.id);
    try {
      if (enable) await googleAdsService.resumeCampaign(accountUid, c.id); else await googleAdsService.pauseCampaign(accountUid, c.id);
      toast.success(enable ? 'Campaign enabled' : 'Campaign paused');
      await load();
    } catch (e) { if (!alreadyToasted(e)) toast.error(`Google refused — ${errMsg(e)}`); } finally { setBusy(null); }
  };

  const subline = (c: GoogleAdsCampaignSummary) => {
    const parts: string[] = [];
    const bid = biddingLabel(c.biddingStrategyType);
    if (bid) parts.push(bid);
    const daily = formatMicros(c.budgetAmountMicros, currency);
    if (daily) parts.push(`${daily} / day`);
    return parts.join(' · ') || 'Search';
  };

  const Icon = cfg.icon;
  const enabledBudget = live.filter((c) => c.status === 'ENABLED').reduce((n, c) => n + Number(c.budgetAmountMicros || 0), 0);
  const stats: Array<{ label: string; value: React.ReactNode; Icon: LucideIcon }> = [
    { label: 'Campaigns', value: live.length, Icon: Megaphone },
    { label: 'Enabled', value: live.filter((c) => c.status === 'ENABLED').length, Icon: Play },
    { label: 'Paused', value: live.filter((c) => c.status === 'PAUSED').length, Icon: Pause },
    cfg.leadForms
      ? { label: 'With a lead form', value: live.filter((c) => leadFormIds.has(c.id)).length, Icon: FileText }
      : { label: 'Daily budget (enabled)', value: formatMicros(enabledBudget, currency) ?? '0', Icon: BarChart3 },
  ];
  const filtering = !!query || statusFilter !== 'all';

  return (
    <>
      <Topbar title={cfg.title} breadcrumb={`Google Ads / ${cfg.title}`} description={cfg.subtitle} />

      <div className="gads-workspace w-full px-5 py-4">
        {auth === undefined && (
          <div className="space-y-3"><div className="skeleton h-14 rounded-2xl" /><Skeleton count={3} /></div>
        )}

        {auth !== undefined && !ready && <ConnectGate auth={auth ?? { connected: false }} accountUid={accountUid} onChanged={checkAuth} />}

        {ready && (
          <div className="w-full pb-10">
            <div className="gads-toolbar mb-3 flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-surface p-3">
              <button type="button" onClick={() => setPickerOpen(true)} title="Switch Google Ads account"
                className="flex min-w-0 items-center gap-2 rounded-full border border-border bg-surface-alt px-3 py-1.5 text-sm text-text-primary transition-colors hover:border-primary/50">
                <Globe size={14} className="shrink-0 text-primary" />
                <span className="max-w-[160px] truncate sm:max-w-[240px]">{auth?.customer_name || auth?.customer_id}</span>
              </button>
              <span className="flex-1" />
              <button type="button" onClick={() => load()} disabled={refreshing} title="Refresh" aria-label="Refresh"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary disabled:opacity-50">
                {refreshing ? <Spinner size={14} /> : <RefreshCw size={15} />}
              </button>
              <Button variant="ghost" size="sm" onClick={() => navigate('/google-ads/tools')} iconLeft={<BarChart3 size={15} />}>Reports</Button>
              <div className="flex h-9 overflow-hidden rounded-lg border border-border">
                <button type="button" onClick={() => setViewPersisted('list')} title="List view" aria-label="List view" aria-pressed={view === 'list'}
                  className={clsx('px-2.5', view === 'list' ? 'bg-surface-alt text-text-primary' : 'text-text-muted hover:bg-surface-alt/50')}><List size={15} /></button>
                <button type="button" onClick={() => setViewPersisted('grid')} title="Grid view" aria-label="Grid view" aria-pressed={view === 'grid'}
                  className={clsx('px-2.5', view === 'grid' ? 'bg-surface-alt text-text-primary' : 'text-text-muted hover:bg-surface-alt/50')}><LayoutGrid size={15} /></button>
              </div>
              <Button onClick={() => navigate(newRoute)} iconLeft={<Plus size={15} />} className="h-9">
                <span className="hidden md:inline">New campaign</span><span className="md:hidden">New</span>
              </Button>
            </div>

            <div className="gads-stats">
              {stats.map(({ label, value, Icon: StatIcon }) => (
                <div className="gads-stat" key={label}>
                  <span className="gads-stat-label">{label}<StatIcon size={17} /></span>
                  <span className="gads-stat-value">{campaigns === null || listError ? '—' : value}</span>
                </div>
              ))}
            </div>

            <div className="gads-filterbar mb-3 flex flex-wrap items-center gap-3">
              <label className="relative min-w-[200px] flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                <Input aria-label="Search campaigns" placeholder="Search campaigns by name or ID…" className="pl-9" value={query} onChange={(e) => setQuery(e.target.value)} />
              </label>
              <label className="flex items-center gap-2">
                <ArrowUpDown size={14} className="text-text-muted" />
                <Select aria-label="Sort campaigns" value={sort} onChange={(e) => setSort(e.target.value as typeof sort)} className="!w-auto">
                  <option value="newest">Newest first</option><option value="name">Name A–Z</option><option value="budget">Daily budget</option>
                </Select>
              </label>
            </div>

            <div className="mb-3 flex items-center justify-between">
              <div className="gads-status-chips">
                <button type="button" aria-pressed={statusFilter === 'all'} onClick={() => setStatusFilter('all')}>All campaigns</button>
                <button type="button" aria-pressed={statusFilter === 'active'} onClick={() => setStatusFilter('active')}>Enabled</button>
                <button type="button" aria-pressed={statusFilter === 'paused'} onClick={() => setStatusFilter('paused')}>Paused</button>
                {removed.length > 0 && <button type="button" aria-pressed={statusFilter === 'removed'} onClick={() => setStatusFilter('removed')}>Removed ({removed.length})</button>}
              </div>
              {filtering && <button type="button" className="text-xs text-info" onClick={() => { setQuery(''); setStatusFilter('all'); }}>Clear filters</button>}
            </div>

            <div className="space-y-3">
              {listError && <Card role="alert" className="border-danger/30 bg-danger-soft p-4 text-sm text-danger">{listError}<button type="button" className="ml-3 underline" onClick={load}>Try again</button></Card>}

              {campaigns === null && !listError && <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="skeleton h-[68px] rounded-2xl" />)}</div>}

              {campaigns !== null && live.length === 0 && statusFilter !== 'removed' && !query && !listError && (
                <Card className="p-12 text-center">
                  <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-soft text-primary"><Icon size={22} /></span>
                  <p className="text-sm font-medium text-text-primary">No {cfg.title.toLowerCase()} campaigns yet</p>
                  <p className="mx-auto mt-1 max-w-sm text-xs text-text-muted">{cfg.emptyHint}</p>
                  <Button className="mt-5" onClick={() => navigate(newRoute)} iconLeft={<Plus size={15} />}>New campaign</Button>
                </Card>
              )}

              {view === 'list' && visible.length > 0 && (
                <div className="gads-campaign-list">
                  {visible.map((c) => {
                    const open = openId === c.id;
                    const st = statusOf(c.status, c.primaryStatus);
                    const cnt = counts?.byCampaign[c.id];
                    return (
                      <Card key={c.id} padded={false} className="gads-campaign-card overflow-hidden rounded-2xl">
                        <div className="gads-campaign-row">
                          <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left" aria-expanded={open} onClick={() => setOpenId(open ? null : c.id)}>
                            {open ? <ChevronDown size={18} className="shrink-0 text-text-muted" /> : <ChevronRight size={18} className="shrink-0 text-text-muted" />}
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-info-soft text-info"><Search size={16} /></span>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="truncate text-[13px] font-semibold leading-tight text-text-primary">{c.name}</span>
                                {leadFormIds.has(c.id) && <Tag tone="ok" className="shrink-0">Lead form</Tag>}
                              </div>
                              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-text-muted">
                                <span className="truncate">{subline(c)}</span>
                                {cnt && (
                                  <span className="inline-flex items-center gap-2.5">
                                    <span className="inline-flex items-center gap-1"><Layers size={12} /> {cnt.adGroups} ad group{cnt.adGroups === 1 ? '' : 's'}</span>
                                    <span className="inline-flex items-center gap-1"><FileText size={12} /> {cnt.ads} ad{cnt.ads === 1 ? '' : 's'}</span>
                                  </span>
                                )}
                                {open && <CampaignDates c={c} />}
                              </div>
                            </div>
                          </button>
                          <Pill {...st} />
                          <Switch checked={c.status === 'ENABLED'} disabled={busy === c.id || c.status === 'REMOVED'} label={`Enable ${c.name}`} onCheckedChange={() => toggle(c)} />
                        </div>
                        {open && (
                          <div className="gads-campaign-detail">
                            <AdGroupRows accountUid={accountUid} customerId={customerId} campaign={c} currency={currency} base={cfg.base} adCounts={counts?.byAdGroup} onChanged={load} />
                            {c.status !== 'REMOVED' && (
                              <div className="pl-9 pt-3">
                                <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2.5 text-sm text-text-secondary hover:text-text-primary"
                                  onClick={() => navigate(`${cfg.base}/create/adgroup?campaign_id=${c.id}`)}>
                                  <Plus size={13} /> New ad group
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}

              {view === 'grid' && visible.length > 0 && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-3">
                  {visible.map((c) => {
                    const st = statusOf(c.status, c.primaryStatus);
                    const ad = previews[c.id];
                    return (
                      <Card key={c.id} padded={false} className="flex flex-col overflow-hidden">
                        <div className="relative border-b border-border bg-surface-alt/50 p-3 pt-9">
                          {ad ? <MiniSerp ad={ad} /> : (
                            <div className="flex h-[74px] items-center justify-center rounded-lg border border-dashed border-border text-text-muted"><Search size={22} /></div>
                          )}
                          <span className="absolute right-2 top-2"><Pill {...st} /></span>
                          {leadFormIds.has(c.id) && <span className="absolute left-2 top-2"><Tag tone="ok">Lead form</Tag></span>}
                        </div>
                        <div className="flex flex-1 flex-col p-3.5">
                          <span className="truncate text-sm font-semibold text-text-primary">{c.name}</span>
                          <div className="mt-0.5 truncate text-xs text-text-muted">{subline(c)}</div>
                          <div className="mt-3 flex items-center gap-2 border-t border-border/50 pt-3">
                            <Switch checked={c.status === 'ENABLED'} disabled={busy === c.id || c.status === 'REMOVED'} label={`Enable ${c.name}`} onCheckedChange={() => toggle(c)} />
                            <span className="text-xs text-text-muted">{c.status === 'ENABLED' ? 'Enabled' : c.status === 'REMOVED' ? 'Removed' : 'Paused'}</span>
                            <span className="flex-1" />
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { setViewPersisted('list'); setOpenId(c.id); }}>Open</Button>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}

              {campaigns !== null && visible.length === 0 && (live.length > 0 || statusFilter === 'removed' || !!query) && (
                <Card className="p-8 text-center text-sm text-text-muted">No campaigns match your filters.</Card>
              )}

              {campaigns !== null && !listError && (
                <p className="flex items-start gap-1.5 px-1 pt-1 text-2xs text-text-muted">
                  <Info size={12} className="mt-px shrink-0" />
                  <span>
                    Campaigns built in {cfg.title} are filed here. Older Search campaigns are sorted by lead form and bidding — the rest are under{' '}
                    <Link to={OTHER[cfg.key].base} className="text-primary hover:underline">{OTHER[cfg.key].title}</Link>.
                  </span>
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {ready && (
        <AttachCustomerDialog open={pickerOpen} accountUid={accountUid} onClose={() => setPickerOpen(false)}
          onAttached={async () => { setPickerOpen(false); await checkAuth(); }} />
      )}
    </>
  );
}
